import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { motion, AnimatePresence } from "framer-motion";
import { User } from "../../features/user-management-module/userAction";

interface UserToggleDialogProps {
  open: boolean;
  onClose: () => void;
  selectedUser: User | null;
  handleDeleteConfirm: () => void;
}

const UserToggleDialog: React.FC<UserToggleDialogProps> = ({
  open,
  onClose,
  selectedUser,
  handleDeleteConfirm,
}) => {
  const theme = useTheme();

  const contentVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
      },
    },
  };

  const buttonVariants = {
    rest: { scale: 1 },
    hover: { scale: 1.05 },
    tap: { scale: 0.95 },
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          style={{
            width: "100%",
            maxWidth: "auto",
            margin: "0 auto",
          }}
        >
          <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            sx={{
              "& .MuiDialog-paper": {
                borderRadius: "12px",
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
                backgroundColor:
                  "linear-gradient(135deg, #edefee 0%, #fcfbf9 100%)",
                [theme.breakpoints.down("sm")]: {
                  margin: "16px",
                  width: "calc(100% - 32px)",
                },
              },
            }}
          >
            <DialogTitle
              sx={{
                color: "#f5f0e7",
                backgroundColor: "#4b5b5d",
                fontWeight: 700,
                fontSize: "1.5rem",
                py: 2,
                px: 3,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              Confirm Action
              <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                <IconButton
                  onClick={onClose}
                  sx={{
                    color: "#f5f0e7",
                    "&:hover": {
                      backgroundColor: "rgba(75, 91, 93, 0.1)",
                    },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </motion.div>
            </DialogTitle>
            <DialogContent
              sx={{
                mt: 4,
                p: 4,
                backgroundColor:
                  "linear-gradient(135deg, #edefee 0%, #fcfbf9 100%)",
                [theme.breakpoints.down("sm")]: {
                  mt: 2,
                  p: 3,
                },
              }}
            >
              <motion.div
                variants={contentVariants}
                initial="hidden"
                animate="visible"
              >
                <Typography
                  variant="body1"
                  color="#4b5b5d"
                  sx={{
                    lineHeight: 1.6,
                  }}
                >
                  Are you sure you want to{" "}
                  {selectedUser?.isActive ? "deactivate" : "activate"} the user
                  "{selectedUser?.firstname} {selectedUser?.lastname}"? This
                  action cannot be undone.
                </Typography>
              </motion.div>
            </DialogContent>
            <DialogActions
              sx={{
                px: 3,
                py: 2,
                backgroundColor: "#f5f0e7",
                borderTop: "1px solid rgba(75, 91, 93, 0.2)",
              }}
            >
              <motion.div
                variants={buttonVariants}
                initial="rest"
                whileHover="hover"
                whileTap="tap"
              >
                <Button
                  onClick={onClose}
                  variant="outlined"
                  sx={{
                    textTransform: "none",
                    borderColor: "#4b5b5d",
                    color: "#4b5b5d",
                    borderRadius: "8px",
                    fontWeight: 600,
                    px: 3,
                    "&:hover": {
                      backgroundColor: "rgba(75, 91, 93, 0.1)",
                      borderColor: "#4b5b5d",
                    },
                  }}
                >
                  Cancel
                </Button>
              </motion.div>
              <motion.div
                variants={buttonVariants}
                initial="rest"
                whileHover="hover"
                whileTap="tap"
              >
                <Button
                  onClick={handleDeleteConfirm}
                  variant="contained"
                  sx={{
                    backgroundColor: "#d32f2f",
                    color: "#f5f0e7",
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 600,
                    px: 3,
                    "&:hover": {
                      backgroundColor: "#b71c1c",
                    },
                  }}
                >
                  {selectedUser?.isActive ? "Deactivate" : "Activate"}
                </Button>
              </motion.div>
            </DialogActions>
          </Dialog>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default UserToggleDialog;
